import { PackColumns, type PackView } from "../../types/models";
import { ItemsTypes, SortingDirection, SortOptions, ViewType } from "enums";
import React, { type ReactNode, type SyntheticEvent, useCallback, useEffect, useMemo } from "react";
import { NoResultsWithButton, NoSearchResults, SearchInput } from "components";
import { Observable } from "utils";
import { type CardsViewerItem } from "components/cardsViewer/types";
import AddToAccountPackCard, {
  type Item,
} from "features/Accounts/PackAssignmentModal/packAssignmentSteps/packGridStep/addToAccountPackCard/AddToAccountPackCard";
import { isEmpty } from "lodash";
import { gridSortingOptions } from "features/Accounts/CreateAccount/CreateAccountPacksList/sortOptions";
import { type DropdownProps, Table } from "semantic-ui-react";
import ItemsView from "views/ItemsView/ItemsView";
import cn from "classnames";
import { HandleOnSelectionChanged, type SelectionChangedArgs } from "interfaces/onSelectionChanged";
import { type PackMap } from "components/assignmentModals/groupTemplatePacksConfirmationModal/types";
import {
  AddTemplateGroupPacksAccountMessageSuccess,
  AddTemplateGroupPacksAccountMessageFailure,
  RemoveAllPackFromTemplateGroupMessageSuccess,
  UpdateAllTemplateGroupPackAccountsMessageSuccess,
} from "features/Application/services/realTimeNotification/events/people/peopleEvents";
import EditPackAccountsModal from "../../Edit/Packs/EditPackAccountsModal";
import { getColumnOptions } from "./getColumnOptions";

type Props = {
  templateId: number;
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
  ownsPacks?: boolean;
  actionButton?: ReactNode;
  items: PackView[];
  isLoading: boolean;
  isAllDataLoaded?: boolean;
  itemsCount: number;
  getGridData: (skip: number, searchTerm: string, orderBy: string) => void;
  getListData: (skip: number, top: number, sortingColumnName: string, sortingDirection: SortingDirection) => void;
  resetGrid?: () => void;
  packMap?: PackMap;
  isCreate: boolean;
  onRemovePackClick?: (id: number) => void;
  className?: string;
  disabled?: boolean;
  preview?: boolean;
};

const columnToParamMap: { [key: string]: string } = {
  [PackColumns.Name.toLowerCase()]: "title",
  [PackColumns.Added.toLowerCase()]: "dateAdded",
};

const rtnEvents = [
  AddTemplateGroupPacksAccountMessageSuccess,
  AddTemplateGroupPacksAccountMessageFailure,
  RemoveAllPackFromTemplateGroupMessageSuccess,
  UpdateAllTemplateGroupPackAccountsMessageSuccess,
];

export const GroupTemplatePacks = (props: Props) => {
  const {
    templateId,
    selectedIds,
    setSelectedIds,
    ownsPacks,
    items,
    isLoading,
    isAllDataLoaded,
    itemsCount,
    getGridData,
    getListData,
    resetGrid,
    packMap,
    isCreate,
    onRemovePackClick,
    actionButton,
    className,
    disabled,
    preview,
  } = props;

  const [search, setSearch] = React.useState("");
  const [gridOrderBy, setGridOrderBy] = React.useState(SortOptions.CreatedDateDesc);
  const onTriggerEditLicensingObserver = useMemo(() => new Observable(), []);

  const isGridView = isCreate;

  const viewType = isGridView ? ViewType.GRID : ViewType.LIST;

  useEffect(() => {
    isGridView && loadPacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, gridOrderBy]);

  const handleEditLicensingClick = useCallback(
    (pack: PackView) => {
      onTriggerEditLicensingObserver.notify(templateId, pack);
    },
    [templateId, onTriggerEditLicensingObserver],
  );

  const loadPacks = useCallback(
    (
      skip: number = 0,
      top: number = 10,
      sortingColumnName: string = PackColumns.Added.toLowerCase(),
      sortingDirection: SortingDirection = SortingDirection.Descending,
    ) => {
      if (isGridView) {
        getGridData(items.length, search, gridOrderBy);
      } else {
        const sortColumn = columnToParamMap[sortingColumnName];
        getListData(skip, top, sortColumn, sortingDirection);
      }
    },
    [isGridView, gridOrderBy, search, getGridData, getListData, items.length],
  );

  const onSortChange = (_: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    resetGrid?.();
    setGridOrderBy(data.value as SortOptions);
  };

  const onSearchChanged = (search: string) => {
    resetGrid?.();
    setSearch(search);
  };

  const onSelectedItemsChanged = (ids: number[]) => {
    setSelectedIds(ids);
  };

  const onSelectionChanged = (args: SelectionChangedArgs<PackView>) => {
    const onAdded = (pack: PackView) => {
      packMap?.set(pack.id, pack);
    };

    const onRemoved = (pack: PackView) => {
      packMap?.delete(pack.id);
    };
    HandleOnSelectionChanged(args, onAdded, onRemoved);
  };

  const renderCard = (props: CardsViewerItem<Item>) => <AddToAccountPackCard {...props} disablePopupMenu={disabled} />;

  const isFiltered = !isEmpty(search);

  const noResultsContent = () => {
    let title, description, actionText;

    if (preview) {
      title = "You don't have any available packs";
      description = "Looks like the owner of this inherited group hasn't added any Packs yet.";
    } else if (ownsPacks) {
      title = "You haven't added any packs";
      description = "Looks like you haven't added any packs to this template yet.";
      actionText = "Add some now using the button below.";
    } else {
      title = "You don't have any available packs";
      description = "Looks like you don't have any packs yet.";
      actionText = "Create one now using the button below.";
    }

    return isFiltered ? (
      <NoSearchResults />
    ) : (
      <NoResultsWithButton
        title={title}
        description={description}
        actionText={actionText}
        iconClassName="fa-box-open"
        actionButton={actionButton}
      />
    );
  };

  const columnOptions = useMemo(
    () =>
      isGridView
        ? []
        : getColumnOptions({
            selectedIds,
            buttonHandlers: { onRemovePackClick, onEditLicensingClick: handleEditLicensingClick },
            disabled,
            preview,
          }),
    [isGridView, selectedIds, onRemovePackClick, handleEditLicensingClick, disabled, preview],
  );

  const buildTableBody = (listItem: PackView, isChecked?: boolean) => (
    <>
      {columnOptions.map(
        (item, index) =>
          !item.isHide && (
            <Table.Cell
              className={cn(item.className)}
              width={item.width}
              key={index}
              disabled={item.disabled?.(listItem)}
            >
              {item.render(listItem, isChecked)}
            </Table.Cell>
          ),
      )}
    </>
  );

  return (
    <>
      <ItemsView
        viewType={viewType}
        itemsType={ItemsTypes.Pack}
        getData={loadPacks}
        columnOptions={isGridView ? undefined : columnOptions}
        buildTableBody={buildTableBody}
        items={items}
        isLoading={isLoading}
        isAllDataLoaded={isAllDataLoaded}
        itemsInlineCount={itemsCount}
        renderCard={isGridView ? renderCard : undefined}
        onSelectedListItemsChanged={onSelectedItemsChanged}
        onSelectedItemsChanged={onSelectedItemsChanged}
        onSelectionChanged={onSelectionChanged}
        selectedIds={selectedIds}
        noResultsContent={noResultsContent()}
        renderSearch={() => (
          <SearchInput placeholder="Search for packs..." onChange={onSearchChanged} disabled={!isCreate} />
        )}
        sortOptions={gridSortingOptions}
        blur
        onSortChange={onSortChange}
        doNotLoadPersistentViewType
        listViewRtnEvents={rtnEvents}
        className={className}
        sortingColumnName={PackColumns.Added}
        isSelectDisabled={() => !!disabled}
      />
      <EditPackAccountsModal onTriggerModalObserver={onTriggerEditLicensingObserver} />
    </>
  );
};
